if (!customElements.get('armourbuy-carousel')) {
  class ArmourbuyCarousel extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.timer = null;
      this.focusFrame = null;
      this.pointerStart = null;
      this.isHovered = false;
      this.hasFocus = false;
      this.userPaused = false;
      this.focusPaused = false;
      this.toggleRequestedAction = null;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktop = window.matchMedia('(min-width: 750px)');
      this.boundVisibility = this.handleVisibility.bind(this);
      this.boundMediaChange = this.syncAutoplay.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.contents = [...this.querySelectorAll('[data-slide-content]')];
      if (this.contents.length !== this.slides.length) return;
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.previousButton = this.querySelector('[data-previous]');
      this.nextButton = this.querySelector('[data-next]');
      this.toggleButton = this.querySelector('[data-toggle]');
      this.toggleLabel = this.querySelector('[data-toggle-label]');
      this.currentLabel = this.querySelector('[data-current]');
      this.progress = this.querySelector('[data-progress]');
      this.status = this.querySelector('[data-status]');
      this.viewport = this.querySelector('[data-viewport]');
      this.interval = Number.parseInt(this.dataset.interval || '6000', 10);
      this.autoplay = this.dataset.autoplay === 'true';

      if (this.slides.length < 2) return;

      this.initialized = true;
      this.controller = new AbortController();
      const listenerOptions = { signal: this.controller.signal };

      this.previousButton?.addEventListener('click', () => this.show(this.index - 1, true), listenerOptions);
      this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true), listenerOptions);
      this.toggleButton?.addEventListener('pointerdown', () => this.captureToggleAction(), listenerOptions);
      this.toggleButton?.addEventListener('click', () => this.toggleAutoplay(), listenerOptions);
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.show(Number.parseInt(dot.dataset.index, 10), true), listenerOptions);
      });

      this.addEventListener('keydown', (event) => this.handleKeydown(event), listenerOptions);
      this.addEventListener('mouseenter', () => {
        this.isHovered = true;
        this.stopAutoplay();
      }, listenerOptions);
      this.addEventListener('mouseleave', () => {
        this.isHovered = false;
        this.syncAutoplay();
      }, listenerOptions);
      this.addEventListener('focusin', () => this.handleFocusIn(), listenerOptions);
      this.addEventListener('focusout', () => {
        if (this.focusFrame) window.cancelAnimationFrame(this.focusFrame);
        this.focusFrame = requestAnimationFrame(() => {
          this.focusFrame = null;
          this.hasFocus = this.contains(document.activeElement);
          this.syncAutoplay();
        });
      }, listenerOptions);

      this.viewport?.addEventListener('pointerdown', (event) => this.handlePointerDown(event), listenerOptions);
      this.viewport?.addEventListener('pointerup', (event) => this.handlePointerUp(event), listenerOptions);
      this.viewport?.addEventListener('pointercancel', () => { this.pointerStart = null; }, listenerOptions);

      document.addEventListener('visibilitychange', this.boundVisibility, listenerOptions);
      document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), listenerOptions);
      this.reduceMotion.addEventListener('change', this.boundMediaChange, listenerOptions);
      this.desktop.addEventListener('change', this.boundMediaChange, listenerOptions);

      this.show(0, false);
      this.syncAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.focusFrame) window.cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
      this.controller?.abort();
      this.controller = null;
      this.initialized = false;
    }

    show(requestedIndex, announce = false) {
      const count = this.slides.length;
      this.index = (requestedIndex + count) % count;
      if (announce) this.userPaused = true;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      this.contents.forEach((content, contentIndex) => {
        const active = contentIndex === this.index;
        content.hidden = !active;
        if (active) {
          content.removeAttribute('aria-hidden');
        } else {
          content.setAttribute('aria-hidden', 'true');
        }
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });

      const humanIndex = this.index + 1;
      if (this.currentLabel) this.currentLabel.textContent = String(humanIndex).padStart(2, '0');
      if (this.progress) {
        this.progress.style.setProperty('--ab-progress', `${(humanIndex / count) * 100}%`);
      }
      if (announce && this.status) this.status.textContent = `Slide ${humanIndex} of ${count}`;

      this.updateToggle();
      this.syncAutoplay();
    }

    toggleAutoplay() {
      const action = this.toggleRequestedAction || this.toggleButton?.dataset.action || 'pause';
      this.toggleRequestedAction = null;

      if (action === 'play') {
        this.userPaused = false;
        this.focusPaused = false;
      } else {
        this.userPaused = true;
        this.focusPaused = false;
      }

      this.updateToggle();
      this.syncAutoplay();
    }

    captureToggleAction() {
      this.toggleRequestedAction = this.toggleButton?.dataset.action || 'pause';
    }

    handleFocusIn() {
      const enteringCarousel = !this.hasFocus;
      this.hasFocus = true;
      if (enteringCarousel) this.focusPaused = true;
      this.updateToggle();
      this.stopAutoplay();
    }

    updateToggle() {
      if (!this.toggleButton) return;

      const available = this.autoplay && this.desktop.matches && !this.reduceMotion.matches;
      this.toggleButton.hidden = !available;
      const persistentlyPaused = this.userPaused || this.focusPaused;
      this.toggleButton.setAttribute('aria-pressed', String(persistentlyPaused));

      const action = persistentlyPaused ? 'Play' : 'Pause';
      this.toggleButton.dataset.action = action.toLowerCase();
      this.toggleButton.setAttribute('aria-label', `${action} carousel`);
      if (this.toggleLabel) this.toggleLabel.textContent = action;
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      const selectedIndex = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (selectedIndex < 0) return;

      this.userPaused = true;
      this.show(selectedIndex, true);
      this.stopAutoplay();
    }

    handleKeydown(event) {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.show(this.index - 1, true);
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.show(this.index + 1, true);
          break;
        case 'Home':
          event.preventDefault();
          this.show(0, true);
          break;
        case 'End':
          event.preventDefault();
          this.show(this.slides.length - 1, true);
          break;
        default:
          break;
      }
    }

    handlePointerDown(event) {
      if (!event.isPrimary) return;
      this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      this.viewport?.setPointerCapture?.(event.pointerId);
    }

    handlePointerUp(event) {
      if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;

      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      this.pointerStart = null;

      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      this.show(this.index + (deltaX < 0 ? 1 : -1), true);
    }

    handleVisibility() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.syncAutoplay();
      }
    }

    canAutoplay() {
      return this.isConnected
        && this.autoplay
        && this.desktop.matches
        && !this.reduceMotion.matches
        && !document.hidden
        && !this.isHovered
        && !this.hasFocus
        && !this.userPaused
        && !this.focusPaused;
    }

    syncAutoplay() {
      this.stopAutoplay();
      this.updateToggle();
      if (!this.canAutoplay()) return;

      this.timer = window.setInterval(() => {
        this.show(this.index + 1, false);
      }, this.interval || 6000);
    }

    stopAutoplay() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('armourbuy-carousel', ArmourbuyCarousel);
}
